import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Stack, Title, Button, Group, Table, Text,
  Select, Alert, Badge, Center,
} from '@mantine/core'
import { IconPlus, IconAlertCircle, IconInbox } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'
import { useCurrentTenant } from '@/store/auth'
import { DataCard, EmptyState } from '@/components/ui'
import {
  GmudClassificationBadge, GmudRiskBadge,
  StatusBadge, ProtocolNumberBadge, opcoesDeStatus,
} from '@/components/chamados'

interface GmudItem {
  id: number
  title: string
  status: string
  priority: string
  protocol_number: string | null
  gmud_classification: string
  risk_level: string
  execution_status: string | null
  requested_by_name: string
  scheduled_start: string | null
  created_at: string
}

interface GmudListResponse {
  items: GmudItem[]
  total: number
}

export function Gmud() {
  const navigate = useNavigate()
  const tenantId = useCurrentTenant()?.id
  const [classification, setClassification] = useState<string | null>(null)
  const [status, setStatus] = useState<string | null>(null)

  const params = new URLSearchParams({ limit: '50' })
  if (classification) params.set('classification', classification)
  if (status) params.set('status', status)

  const { data, isLoading, error } = useQuery<GmudListResponse>({
    queryKey: ['gmud-list', tenantId, classification, status],
    queryFn: async () => {
      const res = await api.get<GmudListResponse>(`/v1/tenants/${tenantId}/gmud?${params}`)
      return res.data
    },
    enabled: !!tenantId,
  })

  const items = data?.items ?? []

  return (
    <Stack gap="md">
      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          Não foi possível carregar as GMUDs. Tente novamente em instantes.
        </Alert>
      )}

      <Group>
        <Select
          size="sm" placeholder="Classificação" clearable
          aria-label="Filtrar por classificação"
          value={classification} onChange={setClassification}
          data={[
            { value: 'standard', label: 'Standard' },
            { value: 'normal', label: 'Normal' },
            { value: 'urgent', label: 'Urgente' },
            { value: 'emergency', label: 'Emergencial' },
          ]}
        />
        <Select
          size="sm" placeholder="Status" clearable
          aria-label="Filtrar por status"
          value={status} onChange={setStatus}
          // Todos os estados da máquina, na ordem do fluxo: faltavam revisão técnica,
          // falhou, revertida e em ratificação — justo onde as GMUDs paradas estavam.
          data={opcoesDeStatus([
            'gmud_draft', 'gmud_submitted', 'gmud_technical_review', 'gmud_pending_cab',
            'gmud_approved', 'gmud_scheduled', 'gmud_executing', 'gmud_completed',
            'gmud_failed', 'gmud_rolled_back', 'gmud_ratification', 'gmud_closed',
          ])}
        />
      </Group>

      <DataCard flush
        title="Gestão de Mudanças (GMUD)"
        accent="primary"
        badge={{ value: data?.total ?? items.length, color: 'blue' }}
        loading={isLoading}
        extra={
          <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => navigate('/gmud/nova')}>
            Nova GMUD
          </Button>
        }
      >
        {items.length === 0 ? (
          <EmptyState
            bare
            size="md"
            accent="primary"
            icon={IconInbox}
            title="Nenhuma GMUD registrada"
            description="Crie uma nova GMUD para gerenciar mudanças com aprovação e janela de execução, ou ajuste os filtros de busca."
            primaryAction={{
              label: 'Nova GMUD',
              icon: <IconPlus size={14} />,
              onClick: () => navigate('/gmud/nova'),
            }}
          />
        ) : (
          // ScrollContainer com minWidth de 1200px: as 7 colunas nao cabem abaixo
          // disso e, sem ele, o Mantine comprimia TODAS as celulas — "Revisão
          // técnica" virava "Rev…" e "medium" virava "me…". Com o protocolo em
          // 24-27 chars (spec 2026-07-28) o aperto piorou. Tabela larga rola no
          // proprio container em vez de esmagar o conteudo.
          <Table.ScrollContainer minWidth={1200}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Protocolo</Table.Th>
                <Table.Th>Título</Table.Th>
                <Table.Th>Classificação</Table.Th>
                <Table.Th>Risco</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Janela</Table.Th>
                <Table.Th>Solicitante</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {items.map((item) => (
                <Table.Tr
                  key={item.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/gmud/${item.id}`)}
                >
                  <Table.Td><ProtocolNumberBadge value={item.protocol_number} /></Table.Td>
                  <Table.Td fw={500}>{item.title}</Table.Td>
                  <Table.Td><GmudClassificationBadge classification={item.gmud_classification} /></Table.Td>
                  <Table.Td><GmudRiskBadge risk={item.risk_level} /></Table.Td>
                  <Table.Td><StatusBadge status={item.status} size="xs" /></Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">
                      {item.scheduled_start
                        ? new Date(item.scheduled_start).toLocaleDateString('pt-BR')
                        : '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td><Text size="xs" c="dimmed">{item.requested_by_name}</Text></Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          </Table.ScrollContainer>
        )}
      </DataCard>
    </Stack>
  )
}
